import {
  Box,
  Container,
  Divider,
  Grid,
  Link as MaterialLink,
  List,
  ListItem,
  ListItemText,
  Typography,
} from '@material-ui/core';
import { Facebook } from '@material-ui/icons';
import React, { useMemo } from 'react';

import { Link } from '@components/Link';
import { Logo } from '@components/Logo';
import { HELP_EMAIL, SocialLink } from '@core/config';
import { useIsXsDown } from '@hooks';
import { priceListUrl, privacyPolicyUrl, regulationUrl } from '@urls';

import useStyles from './styles';

type Link = { title: string; path: string; fullPage?: boolean };

interface FooterProps {
  fullWidth?: boolean;
  mobileEdgeToEdge?: boolean;
}

export const Footer: React.FC<FooterProps> = ({ fullWidth = false, mobileEdgeToEdge = false }) => {
  const classes = useStyles();
  const isXs = useIsXsDown();
  const year = useMemo(() => new Date().getFullYear(), []);

  const LINKS = [
    { title: 'Cennik', path: priceListUrl },
    { title: 'Polityka prywatności', path: privacyPolicyUrl },
    { title: 'Regulamin', path: regulationUrl },
    { title: 'Mapa strony', path: '/sitemap.xml', fullPage: true },
  ];

  return (
    <Box className={classes.root} component="footer">
      <Container
        maxWidth={fullWidth ? false : undefined}
        disableGutters={mobileEdgeToEdge && isXs}
      >
        <Grid container direction="column">
          {isXs ? (
            <>
              <Grid item className={classes.logo}>
                <Logo light />
              </Grid>
              <Grid item className={classes.links}>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <List disablePadding>
                      <ListItem
                        className={classes.listItem}
                        component="a"
                        href={`mailto:${HELP_EMAIL}`}
                      >
                        <ListItemText>Pomoc</ListItemText>
                      </ListItem>
                      <ListItem className={classes.listItem} component="a" href="/sitemap.xml">
                        <ListItemText>Mapa strony</ListItemText>
                      </ListItem>
                    </List>
                  </Grid>
                  <Grid item xs={6}>
                    <List disablePadding>
                      <ListItem className={classes.listItem} component={Link} to={regulationUrl}>
                        <ListItemText>Regulamin</ListItemText>
                      </ListItem>
                      <ListItem className={classes.listItem} component={Link} to={privacyPolicyUrl}>
                        <ListItemText>Polityka prywatności</ListItemText>
                      </ListItem>
                    </List>
                  </Grid>
                </Grid>
              </Grid>
            </>
          ) : (
            <Grid item className={classes.topRow}>
              <Grid container alignItems="center" justify="flex-start" className={classes.topRowInner}>
                <Grid item className={classes.logo}>
                  <Logo light />
                </Grid>
                <Grid item className={classes.desktopLinks}>
                  <List disablePadding className={classes.list}>
                    <ListItem className={classes.listItem} component="a" href={`mailto:${HELP_EMAIL}`}>
                      <ListItemText>Pomoc</ListItemText>
                    </ListItem>
                    {LINKS.map(({ title, path, fullPage }, id) => (
                      <React.Fragment key={id}>
                        <Divider flexItem orientation="vertical" className={classes.divider} />
                        <ListItem
                          className={classes.listItem}
                          component={fullPage ? 'a' : Link}
                          {...(fullPage ? { href: path } : { to: path })}
                        >
                          <ListItemText>{title}</ListItemText>
                        </ListItem>
                      </React.Fragment>
                    ))}
                  </List>
                </Grid>
              </Grid>
            </Grid>
          )}
          <Grid
            container
            direction="row"
            justify="space-between"
            alignItems="center"
            className={classes.bottom}
          >
            <Grid item xs={9}>
              <Typography className={classes.contactDetails} variant="body2" component="div">
                {isXs ? (
                  <>
                    Grzegorz Kłos
                    <br />
                    ul. Ogrodowa 6, 64-850 Ujście
                    <br />
                    NIP: 7642453304, telefon: 602143101, info@pracanakolach.pl
                  </>
                ) : (
                  <>Grzegorz Kłos ul. Ogrodowa 6, 64-850 Ujście NIP: 7642453304, telefon: 602143101, info@pracanakolach.pl</>
                )}
              </Typography>
              <Typography className={classes.copyright} variant="body2">
                Copyright © {year} Praca Na Kołach. Wszelkie prawa zastrzeżone.
              </Typography>
            </Grid>
            <Grid item xs={3} className={classes.social}>
              <MaterialLink href={SocialLink.Facebook} target="_blank">
                <Facebook className={classes.icon} />
              </MaterialLink>
            </Grid>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};
